import { useNavigate, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAuth } from '../context/AuthContext';
import { useDarkMode } from "../context/DarkModeContext";
import Header from './Header'
import CardGrid from '../components/CardGrid';
import Modal from '../components/Modal';
import api from '../api/client';
import "./Profile.css";
import settingsBlack from "../images/settingsBlack.svg"
import settingsWhite from "../images/settingsWhite.svg"
import arrowImg from "../images/arrow.svg"
import editImg from "../images/edit.svg"

const CATEGORIES = [
    'categories.painting',
    'categories.digital',
    'categories.photography',
    'categories.sculpture',
    'categories.drawing',
    'categories.mixedMedia'
];

// Turn a raw User-Agent string into a friendly "Browser on OS" label.
function describeUserAgent(ua) {
    if (!ua) return 'Unknown device';
    let os = 'Unknown OS';
    if (/Windows/i.test(ua)) os = 'Windows';
    else if (/iPhone|iPad|iPod/i.test(ua)) os = 'iOS';
    else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macOS';
    else if (/Android/i.test(ua)) os = 'Android';
    else if (/Linux/i.test(ua)) os = 'Linux';

    let browser = 'browser';
    if (/Edg\//i.test(ua)) browser = 'Edge';
    else if (/OPR\/|Opera/i.test(ua)) browser = 'Opera';
    else if (/Chrome\//i.test(ua)) browser = 'Chrome';
    else if (/Firefox\//i.test(ua)) browser = 'Firefox';
    else if (/Safari\//i.test(ua)) browser = 'Safari';
    return `${browser} on ${os}`;
}

export default function Profile({ lang, setLang, translations }) {
    const { user, refreshProfile, logout } = useAuth();
    const { darkMode, setDarkMode } = useDarkMode();
    let navigate = useNavigate();
    let [editModal, setEditModal] = useState(false);
    let [editLanguageModal, setEditLanguageModal] = useState(false);
    const [username, setUsername] = useState(user?.username || '');
    const [description, setDescription] = useState(user?.description || '');
    const [birthDate, setBirthDate] = useState(
        user?.birthDate ? new Date(user.birthDate).toISOString().slice(0, 10) : ''
    );
    const [city, setCity] = useState(user?.city || '');
    const [showBirthDate, setShowBirthDate] = useState(!!user?.showBirthDate);
    const [showCity, setShowCity] = useState(!!user?.showCity);
    const [followersVisibility, setFollowersVisibility] = useState(user?.followersVisibility || 'public');
    const [followingVisibility, setFollowingVisibility] = useState(user?.followingVisibility || 'public');
    const hasExistingBirthDate = !!user?.birthDate;
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    
    const [loading, setLoading] = useState(true);
    const [pictures, setPictures] = useState([]);
    const [uploading, setUploading] = useState(null);

    const [error, setError] = useState('');

    const [editingPicture, setEditingPicture] = useState(null);
    const [pictureForm, setPictureForm] = useState({ description: '', price: '', category: '' });
    const [pictureSaving, setPictureSaving] = useState(false);
    const [pictureError, setPictureError] = useState('');

    const [passwordModal, setPasswordModal] = useState(false);
    const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [passwordError, setPasswordError] = useState('');
    const [passwordSuccess, setPasswordSuccess] = useState('');

    const [followerCount, setFollowerCount] = useState(0);
    const [followingCount, setFollowingCount] = useState(0);
    const [listModal, setListModal] = useState(null); // 'followers' | 'following' | null
    const [listItems, setListItems] = useState([]);
    const [listLoading, setListLoading] = useState(false);

    const [sessionsModal, setSessionsModal] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [accountModal, setAccountModal] = useState(false);
    const [accountMsg, setAccountMsg] = useState('');
    const [accountBusy, setAccountBusy] = useState(false);

    useEffect(() => {
        api.getOwnPictures(0, 50)
        .then(setPictures)
        .catch(() => setPictures([]))
        .finally(() => setLoading(false));

        api.getFollowers(0, 1).then((r) => setFollowerCount(r?.total || 0)).catch(() => {});
        api.getFollowing(0, 1).then((r) => setFollowingCount(r?.total || 0)).catch(() => {});
    }, []);

    const openList = async (type) => {
        setListModal(type);
        setListItems([]);
        setListLoading(true);
        try {
            const res = type === 'followers'
                ? await api.getFollowers(0, 25)
                : await api.getFollowing(0, 25);
            setListItems(res?.items || []);
        } catch {
            setListItems([]);
        } finally {
            setListLoading(false);
        }
    };

    const handleLogoutSubmit = async () => {
        await logout();
        navigate("/")
    }

    const handleSaveProfile = async (e) => {
        e.preventDefault();
        setError('');
        setSaved(false);
        setSaving(true);
        try {
            const payload = {
                username,
                description,
                city: city.trim(),
                showCity,
                showBirthDate,
                followersVisibility,
                followingVisibility,
            };
            if (!hasExistingBirthDate && birthDate) {
                payload.birthDate = new Date(birthDate).toISOString();
            }
            await api.updateProfile(payload);
            await refreshProfile();
            setSaved(true);
            setEditModal(false);
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    }

    const handleImageUpload = async (type, e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(type);
        try {
            if (type === 'profile') {
                await api.uploadProfilePicture(file);
            } else if (type === 'banner') {
                await api.uploadBannerPicture(file);
            }
            await refreshProfile();
        } catch {
            // Ignore
        } finally {
            setUploading(null);
        }
    };

    const openEditPicture = (picture) => {
        setEditingPicture(picture);
        setPictureForm({
            description: picture.description || '',
            price: picture.price != null ? String(picture.price) : '',
            category: picture.category || CATEGORIES[0],
        });
        setPictureError('');
    };

    const handleSavePicture = async (e) => {
        e.preventDefault();
        if (!editingPicture) return;
        setPictureError('');
        setPictureSaving(true);
        try {
            const payload = {
                description: pictureForm.description.trim(),
                category: pictureForm.category,
            };
            if (pictureForm.price !== '') {
                payload.price = Number(pictureForm.price);
            }
            const updated = await api.updatePicture(editingPicture.id, payload);
            setPictures((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
            setEditingPicture(null);
        } catch (err) {
            setPictureError(err.message);
        } finally {
            setPictureSaving(false);
        }
    };

    const handleDeletePicture = async (picture) => {
        const confirmMsg = translations['profile-delete-confirm'] || 'Delete this post? This cannot be undone.';
        if (!window.confirm(confirmMsg)) return;
        try {
            await api.deletePicture(picture.publicId);
            setPictures((prev) => prev.filter((p) => p.publicId !== picture.publicId));
        } catch (err) {
            setError(err.message);
        }
    };

    const openSessionsModal = async () => {
        setSessionsModal(true);
        setSessionsLoading(true);
        try {
            const data = await api.getSessions();
            setSessions(Array.isArray(data) ? data : []);
        } catch {
            setSessions([]);
        } finally {
            setSessionsLoading(false);
        }
    };

    const handleDeleteSession = async (session) => {
        // The backend matches sessions by their uniqueId (UUID), not the Mongo _id.
        try {
            await api.deleteSession(session.uniqueId);
            setSessions((prev) => prev.filter((s) => s.id !== session.id));
        } catch {
            // Ignore
        }
    };

    const handleLogoutAll = async () => {
        if (!window.confirm(translations['profile-logout-all-confirm'] || 'Log out of all devices?')) return;
        try {
            await api.deleteAllSessions();
            await logout();
            navigate('/');
        } catch {
            // Ignore
        }
    };

    const handleRequestDeletion = async () => {
        setAccountBusy(true);
        setAccountMsg('');
        try {
            const res = await api.requestAccountDeletion();
            setAccountMsg(res?.message || translations['profile-deletion-sent'] || 'Check your email to confirm account deletion.');
        } catch (err) {
            setAccountMsg(err.message);
        } finally {
            setAccountBusy(false);
        }
    };

    const openPasswordModal = () => {
        setPasswordForm({ current: '', next: '', confirm: '' });
        setPasswordError('');
        setPasswordSuccess('');
        setPasswordModal(true);
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setPasswordError('');
        setPasswordSuccess('');
        if (passwordForm.next !== passwordForm.confirm) {
            setPasswordError(translations['profile-password-mismatch'] || 'New passwords do not match.');
            return;
        }
        if (passwordForm.next.length < 8) {
            setPasswordError(translations['profile-password-too-short'] || 'New password must be at least 8 characters.');
            return;
        }
        setPasswordSaving(true);
        try {
            await api.changePassword(passwordForm.current, passwordForm.next);
            setPasswordSuccess(translations['profile-password-changed'] || 'Password changed.');
            setPasswordForm({ current: '', next: '', confirm: '' });
        } catch (err) {
            setPasswordError(err.message);
        } finally {
            setPasswordSaving(false);
        }
    };

    const handleButton = async (id) => {
        if (id === "1") {
            openPasswordModal();
            return;
        }
        if (id === "2") {
            var element = document.body;
            element.classList.toggle("dark-mode");
        }
        if (id === "3") {
            setEditLanguageModal(true);
        }
        console.log("button nb : ", id);
    }

    /*const toggleDarkMode = () => {
        var element = document.body;
        element.classList.toggle("dark-mode");
    }*/

    return (
        <>
            <Header translations={translations} />
            <div className="profile-container">
                <div className="profile-info-box">
                    <label className="profile-banner-wrapper">
                        {user.bannerPicture ? (
                            <img src={user.bannerPicture} alt="" className="profile-banner-img" />
                        ) : (
                            <div className="profile-banner-img profile-banner-fallback" />
                        )}
                        <div className="profile-banner-overlay">
                            {uploading === 'banner' ? <span className="spinner" /> : (translations['profile-edit-banner'] || 'Edit banner')}
                        </div>
                        <input type="file" accept="image/*" hidden onChange={(e) => handleImageUpload('banner', e)} />
                    </label>
                    <div className="profile-card">
                        <label className="profile-avatar-wrapper">
                            {user.profilePicture ? (
                                <img src={user.profilePicture} alt="" className="profile-avatar" />
                            ) : (
                                <div className="profile-avatar profile-avatar-fallback">
                                    {(user.username || '?')[0].toUpperCase()}
                                </div>
                            )}
                            <div className="profile-avatar-overlay">
                                {uploading === 'profile' ? <span className="spinner" /> : 'Edit'}
                            </div>
                            <input type="file" accept="image/*" hidden onChange={(e) => handleImageUpload('profile', e)} />
                        </label>

                        <div className="infos">
                            <span className="profile-username">{user?.username}</span>
                            {(user?.city?.trim()) && (
                                <span className="profile-localisation">
                                    {user?.city}
                                    {user?.showCity === false && (
                                        <em style={{ marginLeft: 6, opacity: 0.7 }}>
                                            ({translations['profile-hidden']})
                                        </em>
                                    )}
                                </span>
                            )}
                            {user?.age != null && (
                                <span className="profile-age">
                                    {user?.age} yrs
                                    {user?.showBirthDate === false && (
                                        <em style={{ marginLeft: 6, opacity: 0.7 }}>
                                            ({translations['profile-hidden']})
                                        </em>
                                    )}
                                </span>
                            )}
                        </div>
                    </div>
                    <p className="profile-description">Description: {user?.description}</p>
                    <div className="profile-follow-stats">
                        <button className="profile-follow-stat" onClick={() => openList('followers')}>
                            <strong>{followerCount}</strong>
                            <span>{translations['profile-followers'] || 'Followers'}</span>
                        </button>
                        <button className="profile-follow-stat" onClick={() => openList('following')}>
                            <strong>{followingCount}</strong>
                            <span>{translations['profile-following'] || 'Following'}</span>
                        </button>
                    </div>
                    <div className="edit-container" onClick={() => {
                        setEditModal(true);
                        setSaved(false);
                    }}>
                        <img src={editImg} alt="edit"  className="edit-img"/>
                    </div>
                </div>
                <div className="parameters-box">
                    <div className="parameters">
                        <img src={darkMode ? settingsWhite : settingsBlack} alt="settings" width="45" height="45"/>
                        <span className="parameters-title">{translations['profile-parameters']}</span>
                    </div>
                    <span className="parameters-email">{localStorage.getItem("email")}</span>
                    <div className="parameters-options">
                        <span>{translations['profile-edit-password']}</span>
                        <img id="1" src={arrowImg} alt="arrow" className="image" onClick={() => handleButton("1")}/>
                    </div>
                    <div className="parameters-options">
                        <span>{translations['profile-dark-mode']}</span>
                        <input
                            className="checkbox-dark-mode"
                            type="checkbox"
                            id="darkmode-toggle"
                            checked={darkMode}
                            onChange={() => setDarkMode(!darkMode)}
                        />
                        <label className="dark-mode" for="darkmode-toggle"></label>
                    </div>
                    <div className="parameters-options">
                        <span>{translations['profile-language']}</span>
                        <img id="3" src={arrowImg} alt="arrow" className="image" onClick={() => handleButton("3")}/>
                    </div>
                    <div className="parameters-options">
                        <span>{translations['profile-accessibility'] || 'Accessibility'}</span>
                        <img src={arrowImg} alt="arrow" className="image" onClick={() => navigate('/accessibility')}/>
                    </div>
                    <div className="parameters-options">
                        <span>
                            {translations['profile-verify'] || 'Verify my identity'}
                            {user?.userType === 'artist' && <span className="profile-verified-badge"> ✓</span>}
                        </span>
                        <img src={arrowImg} alt="arrow" className="image" onClick={() => navigate('/verify-identity')}/>
                    </div>
                    <div className="parameters-options">
                        <span>{translations['profile-support'] || 'Help & Support'}</span>
                        <img src={arrowImg} alt="arrow" className="image" onClick={() => navigate('/support')}/>
                    </div>
                    <div className="parameters-options">
                        <span>{translations['profile-sessions'] || 'Active sessions'}</span>
                        <img src={arrowImg} alt="arrow" className="image" onClick={openSessionsModal}/>
                    </div>
                    <div className="parameters-options">
                        <span>{translations['profile-delete-account'] || 'Delete account'}</span>
                        <img src={arrowImg} alt="arrow" className="image" onClick={() => { setAccountMsg(''); setAccountModal(true); }}/>
                    </div>
                    <div className="center">
                        <button className='btn logout' onClick={handleLogoutSubmit}>{translations['profile-logout']}</button>
                    </div>
                </div>
            </div>
            <div className="profile-pictures">
                <h2 className="profile-pictures-title">
                    {translations['profile-my-posts'] || 'My posts'}
                </h2>
                {loading ? (
                    <div className="spinner" />
                ) : (
                    <CardGrid
                        pictures={pictures}
                        onEdit={openEditPicture}
                        onDelete={handleDeletePicture}
                    />
                )}
            </div>
            {editingPicture && (
                <Modal
                    title={translations['profile-edit-post'] || 'Edit post'}
                    onClose={() => setEditingPicture(null)}
                >
                    <form onSubmit={handleSavePicture}>
                        <div className="app-field">
                            <label className="app-field-label">
                                {translations['picture-field-description'] || 'Description'}
                            </label>
                            <textarea
                                className="app-textarea"
                                value={pictureForm.description}
                                onChange={(e) => setPictureForm((f) => ({ ...f, description: e.target.value }))}
                                placeholder={translations['create-card-description-placeholder'] || 'Describe your artwork'}
                            />
                        </div>
                        <div className="app-field">
                            <label className="app-field-label">
                                {translations['picture-field-price'] || 'Price (€)'}
                            </label>
                            <input
                                type="text"
                                inputMode="decimal"
                                className="app-input"
                                value={pictureForm.price}
                                onChange={(e) => setPictureForm((f) => ({ ...f, price: e.target.value }))}
                                placeholder="0"
                            />
                        </div>
                        <div className="app-field">
                            <label className="app-field-label">
                                {translations['picture-field-category'] || 'Category'}
                            </label>
                            <select
                                className="app-select"
                                value={pictureForm.category}
                                onChange={(e) => setPictureForm((f) => ({ ...f, category: e.target.value }))}
                            >
                                {(CATEGORIES.includes(pictureForm.category) || !pictureForm.category
                                    ? CATEGORIES
                                    : [pictureForm.category, ...CATEGORIES]
                                ).map((cat) => {
                                    const key = cat.split('.')[1];
                                    return (
                                        <option key={cat} value={cat}>
                                            {translations.categories?.[key] || key || cat}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                        {pictureError && <p className="app-form-error">{pictureError}</p>}
                        <button type="submit" className="app-modal-btn" disabled={pictureSaving}>
                            {pictureSaving ? (translations['picture-saving'] || 'Saving...') : (translations['picture-save'] || 'Save')}
                        </button>
                    </form>
                </Modal>
            )}
            {editModal && (
                <Modal
                    title={translations['profile-edit-modal'] || 'Edit Profile'}
                    onClose={() => setEditModal(false)}
                >
                    <form onSubmit={handleSaveProfile}>
                        <div className="app-field">
                            <label className="app-field-label">{translations['profile-username-label'] || 'Username'}</label>
                            <input
                                type="text"
                                className="app-input"
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                placeholder={translations['profile-username-placeholder'] || 'Username'}
                                minLength={3}
                                maxLength={30}
                            />
                        </div>
                        <div className="app-field">
                            <label className="app-field-label">{translations['profile-description-label'] || 'Description'}</label>
                            <textarea
                                value={description}
                                className="app-textarea"
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder={translations['profile-description-placeholder'] || 'Tell people about yourself'}
                            />
                        </div>
                        <div className="app-field">
                            <label className="app-field-label">{translations['profile-city-label'] || 'City'}</label>
                            <input
                                type="text"
                                className="app-input"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                                placeholder={translations['profile-city-placeholder'] || 'City'}
                            />
                        </div>
                        <label className="app-checkbox">
                            <input
                                type="checkbox"
                                checked={showCity}
                                onChange={(e) => { setShowCity(e.target.checked); setSaved(false); }}
                            />
                            <span>{translations['profile-show-city'] || 'Show my city on my public profile'}</span>
                        </label>
                        <div className="app-field">
                            <label className="app-field-label">{translations['profile-birthdate-label'] || 'Birth date'}</label>
                            <input
                                type="date"
                                className="app-input"
                                value={birthDate}
                                onChange={(e) => { setBirthDate(e.target.value); setSaved(false); }}
                                disabled={hasExistingBirthDate}
                                max={new Date().toISOString().slice(0, 10)}
                            />
                            {hasExistingBirthDate && (
                                <span className="app-field-label" style={{ fontWeight: 400, opacity: 0.7 }}>
                                    {translations['profile-birth-date-warning']}
                                </span>
                            )}
                        </div>
                        <label className="app-checkbox">
                            <input
                                type="checkbox"
                                checked={showBirthDate}
                                onChange={(e) => { setShowBirthDate(e.target.checked); setSaved(false); }}
                            />
                            <span>{translations['profile-show-birth-date'] || 'Show my age on my public profile'}</span>
                        </label>
                        <div className="app-field">
                            <label className="app-field-label">{translations['profile-followers-visibility'] || 'Who can see my followers'}</label>
                            <select
                                className="app-select"
                                value={followersVisibility}
                                onChange={(e) => { setFollowersVisibility(e.target.value); setSaved(false); }}
                            >
                                <option value="public">{translations['visibility-public'] || 'Everyone'}</option>
                                <option value="friends">{translations['visibility-friends'] || 'Mutual follows only'}</option>
                                <option value="private">{translations['visibility-private'] || 'Only me'}</option>
                            </select>
                        </div>
                        <div className="app-field">
                            <label className="app-field-label">{translations['profile-following-visibility'] || 'Who can see who I follow'}</label>
                            <select
                                className="app-select"
                                value={followingVisibility}
                                onChange={(e) => { setFollowingVisibility(e.target.value); setSaved(false); }}
                            >
                                <option value="public">{translations['visibility-public'] || 'Everyone'}</option>
                                <option value="friends">{translations['visibility-friends'] || 'Mutual follows only'}</option>
                                <option value="private">{translations['visibility-private'] || 'Only me'}</option>
                            </select>
                        </div>
                        {error && <p className="app-form-error">{error}</p>}
                        {saved && <p className="app-form-success">{translations['profile-updated'] || 'Profile updated'}</p>}
                        <button type="submit" className="app-modal-btn" disabled={saving}>
                            {saving ? (translations['profile-saving'] || 'Saving...') : (translations['profile-save'] || 'Save')}
                        </button>
                    </form>
                </Modal>
            )}
            {passwordModal && (
                <Modal
                    title={translations['profile-edit-password'] || 'Change password'}
                    onClose={() => setPasswordModal(false)}
                >
                    <form onSubmit={handleChangePassword}>
                        <div className="app-field">
                            <label className="app-field-label">{translations['profile-current-password'] || 'Current password'}</label>
                            <input
                                type="password"
                                className="app-input"
                                value={passwordForm.current}
                                onChange={(e) => setPasswordForm((f) => ({ ...f, current: e.target.value }))}
                                autoComplete="current-password"
                            />
                        </div>
                        <div className="app-field">
                            <label className="app-field-label">{translations['profile-new-password'] || 'New password'}</label>
                            <input
                                type="password"
                                className="app-input"
                                value={passwordForm.next}
                                onChange={(e) => setPasswordForm((f) => ({ ...f, next: e.target.value }))}
                                autoComplete="new-password"
                            />
                        </div>
                        <div className="app-field">
                            <label className="app-field-label">{translations['profile-confirm-password'] || 'Confirm new password'}</label>
                            <input
                                type="password"
                                className="app-input"
                                value={passwordForm.confirm}
                                onChange={(e) => setPasswordForm((f) => ({ ...f, confirm: e.target.value }))}
                                autoComplete="new-password"
                            />
                        </div>
                        {passwordError && <p className="app-form-error">{passwordError}</p>}
                        {passwordSuccess && <p className="app-form-success">{passwordSuccess}</p>}
                        <button type="submit" className="app-modal-btn" disabled={passwordSaving}>
                            {passwordSaving ? (translations['profile-saving'] || 'Saving...') : (translations['profile-change-password'] || 'Change password')}
                        </button>
                    </form>
                </Modal>
            )}
            {sessionsModal && (
                <Modal
                    title={translations['profile-sessions'] || 'Active sessions'}
                    onClose={() => setSessionsModal(false)}
                >
                    {sessionsLoading ? (
                        <div className="loading-page"><div className="spinner" /></div>
                    ) : sessions.length === 0 ? (
                        <p className="profile-list-empty">{translations['profile-no-sessions'] || 'No active sessions.'}</p>
                    ) : (
                        <ul className="profile-session-list">
                            {sessions.map((s) => (
                                <li key={s.id} className="profile-session-item">
                                    <div className="profile-session-info">
                                        <span className="profile-session-device">
                                            {describeUserAgent(s.userAgent)}
                                            {s.current && (
                                                <span className="profile-session-current">
                                                    {translations['profile-session-current'] || 'This device'}
                                                </span>
                                            )}
                                        </span>
                                        <span className="profile-session-ip">
                                            {translations['profile-session-ip'] || 'IP'}: {s.ip || '—'}
                                        </span>
                                        <span className="profile-session-date">
                                            {new Date(s.createdAt).toLocaleString()}
                                            {' · '}
                                            {translations['profile-session-expires'] || 'Expires'} {new Date(s.expiresAt).toLocaleDateString()}
                                        </span>
                                    </div>
                                    {!s.current && (
                                        <button className="profile-session-revoke" onClick={() => handleDeleteSession(s)}>
                                            {translations['profile-session-revoke'] || 'Revoke'}
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                    <button className="app-modal-btn" style={{ marginTop: 14 }} onClick={handleLogoutAll}>
                        {translations['profile-logout-all'] || 'Log out of all devices'}
                    </button>
                </Modal>
            )}
            {accountModal && (
                <Modal
                    title={translations['profile-delete-account'] || 'Delete account'}
                    onClose={() => setAccountModal(false)}
                >
                    <p className="profile-delete-warning">
                        {translations['profile-delete-warning'] || 'This will permanently delete your account and all your data. We will email you a link to confirm.'}
                    </p>
                    {accountMsg && <p className="app-form-success">{accountMsg}</p>}
                    <button className="app-modal-btn profile-delete-btn" onClick={handleRequestDeletion} disabled={accountBusy}>
                        {accountBusy
                            ? (translations['profile-saving'] || 'Saving...')
                            : (translations['profile-delete-request'] || 'Send deletion email')}
                    </button>
                </Modal>
            )}
            {listModal && (
                <Modal
                    title={listModal === 'followers'
                        ? (translations['profile-followers'] || 'Followers')
                        : (translations['profile-following'] || 'Following')}
                    onClose={() => setListModal(null)}
                >
                    {listLoading ? (
                        <div className="loading-page"><div className="spinner" /></div>
                    ) : listItems.length === 0 ? (
                        <p className="profile-list-empty">{translations['profile-list-empty'] || 'Nobody here yet.'}</p>
                    ) : (
                        <ul className="profile-user-list">
                            {listItems.map((u) => (
                                <li key={u.publicId}>
                                    <Link
                                        to={`/user/${u.publicId}`}
                                        className="profile-user-list-item"
                                        onClick={() => setListModal(null)}
                                    >
                                        {u.profilePicture ? (
                                            <img src={u.profilePicture} alt="" className="profile-user-avatar" />
                                        ) : (
                                            <span className="profile-user-avatar fallback">
                                                {(u.username || '?')[0].toUpperCase()}
                                            </span>
                                        )}
                                        <span className="profile-user-name">{u.username || 'Unknown'}</span>
                                        {u.isFollowing && (
                                            <span className="profile-user-tag">{translations['profile-following-tag'] || 'Following'}</span>
                                        )}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </Modal>
            )}
            {editLanguageModal && (
                <Modal
                    title={translations['profile-edit-language-modal'] || 'Language'}
                    onClose={() => setEditLanguageModal(false)}
                >
                    <div className="profile-language-options">
                        <button
                            className="app-modal-btn"
                            onClick={() => { setLang("en"); setEditLanguageModal(false); }}
                        >
                            {translations['profile-language-english'] || 'English'}
                        </button>
                        <button
                            className="app-modal-btn"
                            onClick={() => { setLang("fr"); setEditLanguageModal(false); }}
                        >
                            {translations['profile-language-french'] || 'French'}
                        </button>
                    </div>
                </Modal>
            )}
        </>
    );
}